import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import JobCard from '../components/JobCard';
import JobFilter from '../components/JobFilter';
import StatsBar from '../components/StatsBar';
import LoadingSkeleton from '../components/LoadingSkeleton';

const accent = '#00e896';
const STORAGE_KEY = 'statuses_upload';

export default function JobsPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const result   = location.state?.result;

  const [jobs,        setJobs]        = useState([]);
  const [statuses,    setStatuses]    = useState({});
  const [loading,     setLoading]     = useState(true);
  const [error,       setError]       = useState(null);
  const [fromCache,   setFromCache]   = useState(false);
  const [generatedAt, setGeneratedAt] = useState('');
  const [placeholder] = useState(false);
  const [filterTier,  setFilterTier]  = useState('all');
  const [filterStatus,setFilterStatus]= useState('all');
  const [searchQ,     setSearchQ]     = useState('');

  useEffect(() => {
    // Results were passed in from the landing page via navigate(state)
    if (!result) {
      // Someone hit /jobs directly with no data — send them back to upload
      setError('No results to show. Upload a resume and prompt first.');
      setLoading(false);
      return;
    }
    setJobs(result.jobs || []);
    setGeneratedAt(result.generatedAt || '');
    setFromCache(result.fromCache || false);

    const saved = JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}');
    const init  = {};
    (result.jobs || []).forEach(j => {
      init[j.id] = saved[j.id] || 'To Apply';
    });
    setStatuses(init);
    setLoading(false);
  }, [result]);

  const updateStatus = (id, newStatus) => {
    setStatuses(prev => {
      const updated = { ...prev, [id]: newStatus };
      localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
      return updated;
    });
  };

  // Filtered jobs
  const filtered = jobs.filter(j => {
    const s = statuses[j.id] || 'To Apply';
    if (filterTier   !== 'all' && j.tier   !== filterTier)   return false;
    if (filterStatus !== 'all' && s !== filterStatus)         return false;
    if (searchQ) {
      const q = searchQ.toLowerCase();
      return (
        j.company.toLowerCase().includes(q) ||
        j.role.toLowerCase().includes(q) ||
        j.stack?.some(t => t.toLowerCase().includes(q))
      );
    }
    return true;
  });

  const applied    = Object.values(statuses).filter(s => s !== 'To Apply').length;
  const interviews = Object.values(statuses).filter(s => s === 'Interview').length;
  const offers     = Object.values(statuses).filter(s => s === 'Offer').length;

  return (
    <div style={styles.root}>
      <div style={styles.noise} />

      <div style={styles.inner}>

        {/* Top Nav */}
        <nav style={styles.nav} className="fade-up">
          <button style={styles.backBtn} onClick={() => navigate('/')}>
            ← New Search
          </button>
          <div style={styles.navMeta}>
            {fromCache && (
              <span style={styles.cachedBadge}>⚡ Cached</span>
            )}
            {generatedAt && (
              <span style={styles.dateBadge}>{generatedAt}</span>
            )}
          </div>
        </nav>

        {/* Page Header */}
        <header style={styles.header} className="fade-up-1">
          <div style={styles.headerLeft}>
            <span style={{ fontSize: 36 }}>🎯</span>
            <div>
              <div style={{ ...styles.typeTag, color: accent, borderColor: accent + '44', background: accent + '11' }}>
                ● YOUR MATCHES
              </div>
              <h1 style={{ ...styles.title, color: accent }}>
                Matched Jobs
              </h1>
              <p style={styles.subtitle}>
                Generated from your resume &amp; prompt
              </p>
            </div>
          </div>
          <button
            style={{ ...styles.refreshBtn, borderColor: accent, color: accent }}
            onClick={() => navigate('/')}
          >
            + New Search
          </button>
        </header>

        {/* Stats Bar */}
        {!loading && !placeholder && (
          <StatsBar
            total={jobs.length}
            filtered={filtered.length}
            applied={applied}
            interviews={interviews}
            offers={offers}
            accent={accent}
          />
        )}

        {/* Filters */}
        {!loading && !placeholder && (
          <JobFilter
            accent={accent}
            filterTier={filterTier}
            filterStatus={filterStatus}
            searchQ={searchQ}
            onTier={setFilterTier}
            onStatus={setFilterStatus}
            onSearch={setSearchQ}
          />
        )}

        {/* Content */}
        {loading && <LoadingSkeleton />}

        {error && (
          <div style={styles.errorBox}>
            <p style={{ color: '#ff5c5c', marginBottom: 12 }}>⚠ {error}</p>
            <button
              style={{ ...styles.refreshBtn, borderColor: '#ff5c5c', color: '#ff5c5c' }}
              onClick={() => navigate('/')}
            >
              Go Back
            </button>
          </div>
        )}

        {!loading && !error && !placeholder && filtered.length === 0 && (
          <div style={styles.emptyBox}>
            <p style={{ color: '#3a4460' }}>No jobs match your filters.</p>
            <button
              style={{ ...styles.refreshBtn, borderColor: accent, color: accent, marginTop: 12 }}
              onClick={() => { setFilterTier('all'); setFilterStatus('all'); setSearchQ(''); }}
            >
              Clear Filters
            </button>
          </div>
        )}

        {!loading && !error && !placeholder && (
          <div style={styles.jobGrid}>
            {filtered
              .sort((a, b) => b.fitScore - a.fitScore)
              .map((job, i) => (
                <JobCard
                  key={job.id}
                  job={job}
                  accent={accent}
                  status={statuses[job.id] || 'To Apply'}
                  onStatusChange={(s) => updateStatus(job.id, s)}
                  animDelay={i * 0.04}
                />
              ))}
          </div>
        )}

      </div>
    </div>
  );
}

const styles = {
  root: {
    minHeight: '100vh', background: '#07080f',
    position: 'relative', overflow: 'hidden',
  },
  noise: {
    position: 'fixed', inset: 0, zIndex: 0, pointerEvents: 'none',
    backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)' opacity='0.04'/%3E%3C/svg%3E")`,
    backgroundRepeat: 'repeat', backgroundSize: '128px',
  },
  inner: {
    position: 'relative', zIndex: 1,
    maxWidth: 1000, margin: '0 auto',
    padding: '28px 20px', display: 'flex',
    flexDirection: 'column', gap: 24,
  },
  nav: {
    display: 'flex', alignItems: 'center',
    justifyContent: 'space-between',
  },
  backBtn: {
    background: 'none', border: '1px solid #1a1e35',
    color: '#5a6a90', padding: '7px 16px',
    borderRadius: 8, fontSize: 12, cursor: 'pointer',
    transition: 'border-color 0.2s',
  },
  navMeta: { display: 'flex', gap: 10, alignItems: 'center' },
  cachedBadge: {
    fontSize: 10, color: '#5a6a90',
    background: '#0d0f1c', border: '1px solid #1a1e35',
    padding: '3px 10px', borderRadius: 20, letterSpacing: 1,
  },
  dateBadge: {
    fontSize: 10, color: '#3a4460',
    fontFamily: "'JetBrains Mono', monospace",
  },
  header: {
    display: 'flex', alignItems: 'center',
    justifyContent: 'space-between', flexWrap: 'wrap', gap: 16,
  },
  headerLeft: { display: 'flex', gap: 16, alignItems: 'center' },
  typeTag: {
    fontSize: 9, fontWeight: 700, letterSpacing: 2,
    textTransform: 'uppercase', border: '1px solid',
    padding: '3px 10px', borderRadius: 4,
    display: 'inline-block', marginBottom: 6,
  },
  title: {
    fontFamily: "'Syne', sans-serif",
    fontSize: 28, fontWeight: 800, lineHeight: 1.1,
  },
  subtitle: { color: '#5a6a90', fontSize: 13 },
  refreshBtn: {
    background: 'none', border: '1px solid',
    padding: '9px 20px', borderRadius: 8,
    fontSize: 12, fontWeight: 700, cursor: 'pointer',
    transition: 'background 0.2s',
  },
  errorBox: {
    background: '#1a0808', border: '1px solid #3d1010',
    borderRadius: 12, padding: '28px',
    display: 'flex', flexDirection: 'column', alignItems: 'center',
  },
  placeholderBox: {
    background: '#0d0f1c', border: '1px solid #1a1e35',
    borderRadius: 16, padding: '60px 28px',
    display: 'flex', flexDirection: 'column',
    alignItems: 'center', gap: 12, textAlign: 'center',
  },
  emptyBox: {
    textAlign: 'center', padding: '48px 0',
    display: 'flex', flexDirection: 'column', alignItems: 'center',
  },
  jobGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(440px, 1fr))',
    gap: 16,
  },
};